import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    TextField,
    CircularProgress,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    Chip,
    InputLabel,
    Grid,
    Paper,
    Divider,
    Menu
} from "@mui/material";
import {
    CloudUpload,
    CloudDownload,
    Apple,
    Computer,
    Delete,
    History,
    Download,
    Terminal,
    Window,
    KeyboardArrowDown,
    ContentCopy,
    Check
} from "@mui/icons-material";
import {
    uploadAppFile,
    getLatestAppFiles,
    deleteAppFile,
    getAllVersionsByPlatform,
} from "../../services/appDownload.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { usePermissions } from "../../context/PermissionProvider";
import Styles from "./AppDownloadUpload.module.scss";
import ConfirmDelete_Icon from "../../assets/images/CommonTableImg/ConfirmDelete_Icon.svg";
import LMT_Logo from "../../assets/images/LMT_Logo.svg";
import { Dialog } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

// Detect the user's operating system so we can surface the matching installer first.
const detectUserOS = () => {
    if (typeof navigator === "undefined") return null;
    const platform = (
        navigator.userAgentData?.platform ||
        navigator.platform ||
        navigator.userAgent ||
        ""
    ).toLowerCase();

    if (platform.includes("win")) return "Windows";
    if (platform.includes("mac")) return "Mac";
    // Exclude Android (which also reports "linux") since there's no Android build.
    if (platform.includes("linux") && !platform.includes("android")) return "Linux";
    return null;
};

// Best-effort guess of the Mac CPU family (Apple Silicon vs Intel). There is no
// direct browser API — both report "MacIntel" — so we read the WebGL GPU
// renderer string ("Apple GPU" => Apple Silicon, "Intel"/"AMD" => Intel).
// Returns "Apple Silicon" | "Intel" | null (unknown). This cannot tell Intel
// from "Intel (Legacy)" since that's a macOS-version distinction, not the chip.
const detectMacArchitectureViaWebGL = () => {
    try {
        if (typeof document === "undefined") return null;
        const canvas = document.createElement("canvas");
        const gl =
            canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
        if (!gl) return null;
        const dbg = gl.getExtension("WEBGL_debug_renderer_info");
        const renderer = dbg
            ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "")
            : "";
        const r = renderer.toLowerCase();
        if (r.includes("apple")) return "Apple Silicon";
        if (r.includes("intel") || r.includes("amd") || r.includes("radeon")) return "Intel";
        return null;
    } catch {
        return null;
    }
};

// "Intel (Legacy)" is a macOS-VERSION distinction, not a chip one: pre-Big Sur Macs
// (10.13–10.15) can't run the modern build and need the legacy one. The only reliable
// version signal is UA Client Hints' platformVersion — the legacy UA string is frozen
// at 10_15_7 by browsers, so we never infer legacy from it. A missing/uncertain signal
// returns false (treat as modern) to avoid mis-routing a current Mac to the legacy build.
const isLegacyMacVersion = (platformVersion) => {
    if (!platformVersion) return false;
    const parts = String(platformVersion).split(".").map((n) => parseInt(n, 10));
    const major = parts[0];
    const minor = parts[1] || 0;
    if (!Number.isFinite(major)) return false;
    if (major < 10) return true;          // ancient
    if (major === 10) return minor <= 15; // 10.13–10.15 legacy; 10.16 is Big Sur (modern)
    return false;                         // 11+ = modern
};

// Fallback for browsers WITHOUT UA Client Hints (Safari, Firefox) — the very browsers
// old Macs run. Parse the macOS version from the UA string. Browsers freeze the UA at
// 10_15_7 as a privacy floor, so only versions BELOW that floor (<= 10.14) are an
// unambiguously real old Mac; we never infer legacy from 10.15 to avoid mis-flagging a
// modern Mac whose UA is frozen. Any Mac this old is necessarily Intel (Apple Silicon
// started at macOS 11), so a match means "Intel (Legacy)".
const legacyMacFromUserAgent = () => {
    if (typeof navigator === "undefined") return false;
    const m = /Mac OS X 10[._](\d+)/.exec(navigator.userAgent || "");
    if (!m) return false;
    const minor = parseInt(m[1], 10);
    return Number.isFinite(minor) && minor <= 14;
};

// Build variants per platform. Mac ships three architecture builds; the others
// ship a single "Standard" build (kept for a uniform data model).
const PLATFORM_VARIANTS = {
    Windows: ["Standard"],
    Mac: ["Apple Silicon", "Intel", "Intel (Legacy)"],
    Linux: ["Standard"],
};

// Short helper text shown under each Mac variant so users pick the right build.
const VARIANT_HINTS = {
    "Apple Silicon": "M1 / M2 / M3 / M4 Macs",
    Intel: "Intel-based Macs",
    "Intel (Legacy)": "Older Intel Macs / earlier macOS",
};

// Accepted installer file types per platform, so the OS file dialog clearly
// offers them (e.g. .dmg on macOS builds). octet-stream/* covers binaries
// that the browser can't infer a MIME type for.
const ACCEPT_BY_PLATFORM = {
    Mac: ".dmg,.pkg,.zip,application/x-apple-diskimage,application/octet-stream",
    Windows: ".exe,.msi,.zip,application/x-msdownload,application/octet-stream",
    Linux: ".AppImage,.deb,.rpm,.tar.gz,.tar,.gz,.zip,application/octet-stream",
};

const getVariantsForPlatform = (platform) => PLATFORM_VARIANTS[platform] || ["Standard"];
const variantOf = (record) => record?.variant || "Standard";

const defaultVariantFor = (platform) => getVariantsForPlatform(platform)[0];

// Stable, public endpoint that always 302-redirects to the latest Linux .deb on
// S3. Linux users install from the terminal instead of using a download button.
const LINUX_INSTALL_URL =
    "https://api.lamynaals.com/api/lmt/downloadLatestLinux";
const LINUX_INSTALL_COMMANDS = `wget -O time-logger.deb ${LINUX_INSTALL_URL}\nsudo apt install ./time-logger.deb`;

const AppDownloadUpload = () => {
    const { canAdd, canDelete } = usePermissions();
    const canUpload = canAdd("appDownload");
    const canRemove = canDelete("appDownload");
    const isAdmin = canUpload || canRemove;
    const userOS = detectUserOS();
    // Best-effort detected Mac chip family: "Apple Silicon" | "Intel" | null.
    const [macArch, setMacArch] = useState(null);
    const [appFiles, setAppFiles] = useState({
        Mac: null,
        Linux: null,
        Windows: null,
    });
    const [allVersions, setAllVersions] = useState({
        Mac: [],
        Linux: [],
        Windows: [],
    });
    const [selectedVersions, setSelectedVersions] = useState({
        Mac: null,
        Linux: null,
        Windows: null,
    });
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState({
        Mac: false,
        Linux: false,
        Windows: false,
    });
    const [versions, setVersions] = useState({
        Mac: "",
        Linux: "",
        Windows: "",
    });
    const [selectedFiles, setSelectedFiles] = useState({
        Mac: null,
        Linux: null,
        Windows: null,
    });
    // Currently chosen build variant per platform (download view).
    const [selectedVariants, setSelectedVariants] = useState({
        Mac: defaultVariantFor("Mac"),
        Linux: defaultVariantFor("Linux"),
        Windows: defaultVariantFor("Windows"),
    });
    // Build variant chosen in the upload form (admin view).
    const [uploadVariants, setUploadVariants] = useState({
        Mac: defaultVariantFor("Mac"),
        Linux: defaultVariantFor("Linux"),
        Windows: defaultVariantFor("Windows"),
    });
    const [copiedLinux, setCopiedLinux] = useState(false);

    useEffect(() => {
        fetchLatestFiles();
    }, []);

    // Detect the Mac chip family (Apple Silicon vs Intel) to surface the right
    // build first. WebGL is the primary signal; UA Client Hints (Chromium) is a
    // secondary confirmation. Best-effort only — falls back to showing all builds.
    useEffect(() => {
        if (userOS !== "Mac") return;
        let cancelled = false;

        const glGuess = detectMacArchitectureViaWebGL();
        if (glGuess && !cancelled) setMacArch(glGuess);

        const uaLegacy = legacyMacFromUserAgent();

        if (navigator.userAgentData?.getHighEntropyValues) {
            navigator.userAgentData
                .getHighEntropyValues(["architecture", "platformVersion"])
                .then((ua) => {
                    if (cancelled || !ua?.architecture) return;
                    const legacy = isLegacyMacVersion(ua.platformVersion);
                    // UA-CH is authoritative for the chip AND carries the real macOS
                    // version, so always apply it — it also refines a WebGL "Intel" guess
                    // into "Intel (Legacy)" on pre-Big Sur Macs (which need the legacy build).
                    if (ua.architecture === "arm") {
                        setMacArch("Apple Silicon");
                    } else if (ua.architecture === "x86") {
                        setMacArch(legacy ? "Intel (Legacy)" : "Intel");
                    }
                })
                .catch(() => {});
        } else if (uaLegacy && !cancelled) {
            // Safari / Firefox on an old Mac: no UA Client Hints, but the UA string reports a
            // real pre-Big Sur version (<= 10.14). Such Macs are always Intel → Intel (Legacy).
            setMacArch("Intel (Legacy)");
        }

        return () => {
            cancelled = true;
        };
    }, [userOS]);

    useEffect(() => {
        if (appFiles) {
            fetchAllVersions("Mac");
            fetchAllVersions("Linux");
            fetchAllVersions("Windows");
        }
    }, [isAdmin]);

    const fetchLatestFiles = async () => {
        setLoading(true);
        try {
            // The axios interceptor unwraps the HTTP body, and the backend
            // double-wraps ({ data: { data: <payload> } }), so the payload is
            // at response.data.data.
            const response = await getLatestAppFiles();
            if (response.data && response.data.data) {
                setAppFiles(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching app files:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllVersions = async (platform) => {
        try {
            const response = await getAllVersionsByPlatform(platform);
            if (response.data && !response.data.error) {
                const list = response.data.data || [];
                setAllVersions((prev) => ({
                    ...prev,
                    [platform]: list,
                }));
                // Default to the latest version of the currently selected variant;
                // if that variant has no build yet (e.g. Mac with only Intel /
                // Legacy uploads), fall back to the latest version overall so the
                // Manage Versions dropdown isn't left empty.
                setSelectedVersions((prev) => {
                    const variant = selectedVariants[platform] || defaultVariantFor(platform);
                    const latestForVariant =
                        list.find((v) => variantOf(v) === variant) || list[0] || null;
                    return { ...prev, [platform]: latestForVariant };
                });
            }
        } catch (error) {
            console.error(`Error fetching ${platform} versions:`, error);
        }
    };

    // One hidden <input type="file"> per platform, triggered via ref. This is
    // more reliable than a nested input inside a MUI Button label.
    const fileInputRefs = useRef({});

    const handleFileSelect = (platform, event) => {
        console.log("📁 [handleFileSelect] platform:", platform, "files:", event.target.files);
        const file = event.target.files && event.target.files[0];
        if (file) {
            console.log("✅ [handleFileSelect] selected file:", {
                name: file.name,
                size: file.size,
                type: file.type,
            });
            setSelectedFiles((prev) => ({ ...prev, [platform]: file }));
        } else {
            console.warn("⚠️ [handleFileSelect] no file picked for", platform);
        }
        // Reset so selecting the same file again still fires onChange.
        event.target.value = "";
    };

    const handleVersionChange = (platform, value) => {
        setVersions((prev) => ({ ...prev, [platform]: value }));
    };

    const handleVersionSelect = (platform, versionId) => {
        const version = allVersions[platform].find((v) => v._id === versionId);
        setSelectedVersions((prev) => ({ ...prev, [platform]: version }));
    };

    // Download view: choosing a build variant (e.g. which Mac) resets the
    // version selection to the latest build available for that variant.
    const handleVariantSelect = (platform, variant) => {
        setSelectedVariants((prev) => ({ ...prev, [platform]: variant }));
        const latestForVariant = (allVersions[platform] || []).find(
            (v) => variantOf(v) === variant
        );
        setSelectedVersions((prev) => ({ ...prev, [platform]: latestForVariant || null }));
    };

    const handleUpload = async (platform) => {
        console.log("⬆️ [handleUpload] start", {
            platform,
            file: selectedFiles[platform]?.name,
            version: versions[platform],
            variant: uploadVariants[platform],
        });
        if (!selectedFiles[platform]) {
            ToastError("Please select a file");
            return;
        }
        if (!versions[platform]) {
            ToastError("Please enter a version");
            return;
        }

        const variant = uploadVariants[platform] || defaultVariantFor(platform);

        // Duplicate check is per platform + variant (same version can exist for
        // different Mac builds, e.g. Apple Silicon v1.0.0 and Intel v1.0.0).
        const isDuplicate = allVersions[platform]?.some(
            (v) => v.version === versions[platform] && variantOf(v) === variant
        );

        if (isDuplicate) {
            ToastError(
                `Version ${versions[platform]} already exists for ${platform}${platform === "Mac" ? ` (${variant})` : ""}`
            );
            return;
        }

        setUploadLoading((prev) => ({ ...prev, [platform]: true }));
        try {
            const formData = new FormData();
            const bodyData = {
                platform,
                version: versions[platform],
                variant,
            };
            formData.append("bodyData", JSON.stringify(bodyData));
            formData.append("file", selectedFiles[platform]);

            console.log("⬆️ [handleUpload] sending request…", bodyData);
            const response = await uploadAppFile(formData);
            console.log("⬆️ [handleUpload] response:", response);
            if (response.data && !response.data.error) {
                ToastSuccess(`${platform} app uploaded successfully`);
                setSelectedFiles((prev) => ({ ...prev, [platform]: null }));
                setVersions((prev) => ({ ...prev, [platform]: "" }));
                fetchLatestFiles();
                fetchAllVersions(platform);
            } else {
                ToastError(response?.data?.message || "Upload failed");
            }
        } catch (error) {
            console.error("❌ [handleUpload] error:", error);
            ToastError(error?.message || error?.response?.data?.message || "Upload failed");
        } finally {
            setUploadLoading((prev) => ({ ...prev, [platform]: false }));
        }
    };

    const handleDownload = (platform, specificFile = null) => {
        const file = specificFile || (isAdmin ? appFiles[platform] : selectedVersions[platform]);

        if (file && file.file && file.file.location) {
            window.open(file.file.location, "_blank");
        } else {
            ToastError("No file available for download");
        }
    };

    const [openDeletePopup, setOpenDeletePopup] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    // "All versions" dropdown menu (customer download view).
    const [versionMenu, setVersionMenu] = useState({ anchorEl: null, rowKey: null });

    const handleDeleteClick = (platform, versionId) => {
        setDeleteTarget({ platform, versionId });
        setOpenDeletePopup(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const { platform, versionId } = deleteTarget;

        try {
            const response = await deleteAppFile(versionId);
            if (response.data && !response.data.error) {
                ToastSuccess(`Version deleted successfully`);
                fetchLatestFiles();
                fetchAllVersions(platform);
                // Clear selection or select next available
                const remaining = allVersions[platform].filter(v => v._id !== versionId);
                if (remaining.length > 0) {
                    setSelectedVersions((prev) => ({ ...prev, [platform]: remaining[0] }));
                } else {
                    setSelectedVersions((prev) => ({ ...prev, [platform]: null }));
                }
            }
        } catch (error) {
            ToastError("Delete failed");
        } finally {
            setOpenDeletePopup(false);
            setDeleteTarget(null);
        }
    };

    const getPlatformIcon = (platform) => {
        switch (platform) {
            case "Mac":
                return <Apple sx={{ fontSize: 24, color: "#008080" }} />;
            case "Linux":
                return <Terminal sx={{ fontSize: 24, color: "#008080" }} />; // Changed to Terminal for Linux
            case "Windows":
                return <Computer sx={{ fontSize: 24, color: "#008080" }} />;
            default:
                return null;
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    // ── Customer download view (Claude-style) ────────────────────────────────
    // Display metadata per platform; an icon factory so colour can be themed.
    const PLATFORM_META = {
        Mac: { label: "macOS", icon: (c) => <Apple sx={{ fontSize: 22, color: c }} /> },
        Windows: { label: "Windows", icon: (c) => <Window sx={{ fontSize: 22, color: c }} /> },
        Linux: { label: "Linux", icon: (c) => <Terminal sx={{ fontSize: 22, color: c }} /> },
    };

    // Latest build for a given platform + variant (versions arrive newest-first).
    const latestForVariant = (platform, variant) => {
        const list = allVersions[platform] || [];
        const filtered = variant ? list.filter((v) => variantOf(v) === variant) : list;
        return filtered[0] || null;
    };

    // All builds for a platform + variant, newest-first (for the version menu).
    const versionsForVariant = (platform, variant) => {
        const list = allVersions[platform] || [];
        return variant ? list.filter((v) => variantOf(v) === variant) : list;
    };

    // Flatten platforms into download rows; Mac expands into its build variants.
    // The user's detected OS is ordered first and flagged as recommended.
    const buildDownloadRows = () => {
        const rows = [];
        const order = userOS
            ? [userOS, ...["Mac", "Windows", "Linux"].filter((p) => p !== userOS)]
            : ["Mac", "Windows", "Linux"];
        order.forEach((platform) => {
            const recommended = platform === userOS;
            const variants = getVariantsForPlatform(platform);
            if (variants.length > 1) {
                variants.forEach((variant) => {
                    rows.push({
                        key: `${platform}-${variant}`,
                        platform,
                        variant,
                        recommended,
                        label: `${PLATFORM_META[platform].label} · ${variant}`,
                        hint: VARIANT_HINTS[variant],
                        latest: latestForVariant(platform, variant),
                    });
                });
            } else {
                rows.push({
                    key: platform,
                    platform,
                    variant: null,
                    recommended,
                    label: PLATFORM_META[platform].label,
                    hint: null,
                    latest: latestForVariant(platform, null),
                });
            }
        });
        return rows;
    };

    const downloadRecord = (record) => {
        if (record && record.file && record.file.location) {
            window.open(record.file.location, "_blank");
        } else {
            ToastError("No build available for download yet");
        }
    };

    const handleCopyLinuxCommands = async () => {
        try {
            await navigator.clipboard.writeText(LINUX_INSTALL_COMMANDS);
            setCopiedLinux(true);
            ToastSuccess("Commands copied to clipboard");
            setTimeout(() => setCopiedLinux(false), 2000);
        } catch (error) {
            ToastError("Could not copy commands");
        }
    };

    const renderDownloadRow = (row) => {
        const allForRow = versionsForVariant(row.platform, row.variant);
        const hasOlder = allForRow.length > 1;

        // Linux ships via the terminal (apt), not a download button. Show the
        // copy-paste install commands; the URL always resolves to the latest .deb.
        if (row.platform === "Linux" && row.latest) {
            return (
                <div
                    className={`${Styles.dlRow} ${row.recommended ? Styles.dlRowRecommended : ""}`}
                    key={row.key}
                    style={{ flexWrap: "wrap", alignItems: "flex-start" }}
                >
                    <div className={Styles.dlRowIcon}>
                        {PLATFORM_META[row.platform].icon("#008080")}
                    </div>
                    <div className={Styles.dlRowInfo}>
                        <span className={Styles.dlRowLabel}>
                            {row.label}
                            {row.recommended && <span className={Styles.dlRowBadge}>For your device</span>}
                        </span>
                        <span className={Styles.dlRowHint}>
                            {`Latest v${row.latest.version} · install from the terminal`}
                        </span>
                    </div>
                    <div style={{ flexBasis: "100%", marginTop: 12, position: "relative" }}>
                        <div
                            style={{
                                background: "#1E1E1E",
                                color: "#E4E4E7",
                                borderRadius: 8,
                                padding: "12px 44px 12px 14px",
                                fontFamily: "monospace",
                                fontSize: 12.5,
                                lineHeight: 1.7,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                            }}
                        >
                            <span style={{ color: "#4ADE80" }}>$ </span>
                            wget -O time-logger.deb {LINUX_INSTALL_URL}{"\n"}
                            <span style={{ color: "#4ADE80" }}>$ </span>
                            sudo apt install ./time-logger.deb
                            <button
                                onClick={handleCopyLinuxCommands}
                                title={copiedLinux ? "Copied!" : "Copy"}
                                style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: copiedLinux ? "#4ADE80" : "#A1A1AA",
                                    display: "flex",
                                    padding: 4,
                                }}
                            >
                                {copiedLinux ? (
                                    <Check sx={{ fontSize: 18 }} />
                                ) : (
                                    <ContentCopy sx={{ fontSize: 18 }} />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={`${Styles.dlRow} ${row.recommended ? Styles.dlRowRecommended : ""}`} key={row.key}>
                <div className={Styles.dlRowIcon}>
                    {PLATFORM_META[row.platform].icon("#008080")}
                </div>
                <div className={Styles.dlRowInfo}>
                    <span className={Styles.dlRowLabel}>
                        {row.label}
                        {row.recommended && <span className={Styles.dlRowBadge}>For your device</span>}
                    </span>
                    <span className={Styles.dlRowHint}>
                        {row.latest
                            ? `Latest v${row.latest.version}${row.hint ? ` · ${row.hint}` : ""}`
                            : row.hint || "No build available yet"}
                    </span>
                </div>
                {hasOlder && (
                    <button
                        className={Styles.dlVersionBtn}
                        onClick={(e) => setVersionMenu({ anchorEl: e.currentTarget, rowKey: row.key })}
                    >
                        Versions <KeyboardArrowDown sx={{ fontSize: 16 }} />
                    </button>
                )}
                <button
                    className={Styles.dlRowBtn}
                    disabled={!row.latest}
                    onClick={() => downloadRecord(row.latest)}
                >
                    <Download sx={{ fontSize: 16 }} /> Download
                </button>

                <Menu
                    anchorEl={versionMenu.anchorEl}
                    open={versionMenu.rowKey === row.key && Boolean(versionMenu.anchorEl)}
                    onClose={() => setVersionMenu({ anchorEl: null, rowKey: null })}
                    PaperProps={{ sx: { border: "1px solid #EAECF0", boxShadow: "0 8px 24px rgba(16,24,40,0.12)" } }}
                >
                    {allForRow.map((v) => (
                        <MenuItem
                            key={v._id}
                            onClick={() => {
                                downloadRecord(v);
                                setVersionMenu({ anchorEl: null, rowKey: null });
                            }}
                            sx={{ fontSize: 13 }}
                        >
                            v{v.version}
                            <Typography component="span" sx={{ ml: 1.5, fontSize: 11, color: "#9CA3AF" }}>
                                {formatDate(v.uploadedAt)}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            </div>
        );
    };

    const renderDownloadView = () => {
        const rows = buildDownloadRows();
        // Primary + alternate buttons for the user's detected OS. The browser
        // can't tell which Mac architecture (Apple Silicon / Intel / Legacy) the
        // visitor has, so prefer whichever detected build actually has an upload
        // — e.g. on an older Intel/Legacy Mac, surface the available build
        // instead of defaulting to an empty "Apple Silicon" button.
        const detected = userOS ? rows.filter((r) => r.platform === userOS) : [];
        const detectedWithBuild = detected.filter((r) => r.latest);
        // Order builds so the detected Mac chip family comes first (Apple Silicon
        // detected => Apple Silicon build first; Intel detected => Intel, then the
        // Legacy Intel build). We surface every build regardless and let the user
        // pick (first = primary/filled, rest = outlined).
        const variantRank = (variant) => {
            if (macArch === "Apple Silicon") return variant === "Apple Silicon" ? 0 : 1;
            if (macArch === "Intel (Legacy)") {
                if (variant === "Intel (Legacy)") return 0;
                if (variant === "Intel") return 1;
                return 2;
            }
            if (macArch === "Intel") {
                if (variant === "Intel") return 0;
                if (variant === "Intel (Legacy)") return 1;
                return 2;
            }
            return 0; // unknown chip => keep original order
        };
        const orderedBuilds = macArch
            ? [...detectedWithBuild].sort((a, b) => variantRank(a.variant) - variantRank(b.variant))
            : detectedWithBuild;
        const heroButtons = orderedBuilds.length ? orderedBuilds : detected.slice(0, 1);
        // True when the detected OS ships more than one build (i.e. Mac).
        const detectedHasVariants =
            userOS && getVariantsForPlatform(userOS).length > 1;

        return (
            <div className={Styles.dlPage}>
                <div className={Styles.dlTopbar}>
                    <img src={LMT_Logo} alt="LMT" className={Styles.dlLogo} />
                    <span className={Styles.dlTopbarText}>Download</span>
                </div>

                <div className={Styles.dlHero}>
                    <h1 className={Styles.dlTitle}>Download the App</h1>
                    <p className={Styles.dlSubtitle}>
                        Get the desktop application for your device, all in one place.
                    </p>

                    <div className={Styles.dlHeroButtons}>
                        {heroButtons.length > 0 ? (
                            <>
                                <div className={Styles.dlHeroBtnRow}>
                                    {heroButtons.map((row, idx) =>
                                        idx === 0 ? (
                                            <button
                                                key={row.key}
                                                className={Styles.dlPrimaryBtn}
                                                disabled={!row.latest}
                                                onClick={() => downloadRecord(row.latest)}
                                            >
                                                {PLATFORM_META[row.platform].icon("#ffffff")}
                                                {row.latest ? `Download for ${row.label}` : `${row.label} — coming soon`}
                                            </button>
                                        ) : (
                                            <button
                                                key={row.key}
                                                className={Styles.dlAltBtn}
                                                disabled={!row.latest}
                                                onClick={() => downloadRecord(row.latest)}
                                            >
                                                {PLATFORM_META[row.platform].icon("#008080")}
                                                {row.variant || row.label}
                                            </button>
                                        )
                                    )}
                                </div>
                                {detectedHasVariants && (
                                    <p className={Styles.dlHeroHint}>
                                        {macArch
                                            ? `Looks like an ${macArch} Mac — that build is shown first. Not right? Pick another below.`
                                            : "Not sure which Mac you have? Pick the build that matches your chip — or see all options below."}
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className={Styles.dlNoDetect}>Choose your platform below to get started.</p>
                        )}
                    </div>
                </div>

                <div className={Styles.dlDivider} />

                <div className={Styles.dlGetStarted}>
                    <div className={Styles.dlGetStartedLeft}>
                        <h2 className={Styles.dlSectionTitle}>Get started</h2>
                        <p className={Styles.dlSectionDesc}>Access the app on all your devices.</p>
                    </div>

                    <div className={Styles.dlCard}>
                        <h3 className={Styles.dlCardTitle}>Desktop</h3>
                        <p className={Styles.dlCardDesc}>
                            Install on macOS, Windows, or Linux to get things done.
                        </p>
                        <div className={Styles.dlRows}>
                            {rows.map((row) => renderDownloadRow(row))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPlatformCard = (platform, recommended = false) => {
        const file = appFiles[platform];
        const hasFile = file && file.file;
        const platformVersions = allVersions[platform] || [];
        const selectedVersion = selectedVersions[platform];
        const variants = getVariantsForPlatform(platform);
        const hasVariants = variants.length > 1; // true only for Mac
        const selectedVariant = selectedVariants[platform] || defaultVariantFor(platform);
        // Download view: only show versions for the chosen build variant.
        const downloadVersions = hasVariants
            ? platformVersions.filter((v) => variantOf(v) === selectedVariant)
            : platformVersions;

        return (
            <div
                className={`${Styles.platformCard} ${recommended ? Styles.recommended : ""}`}
                key={platform}
            >
                <div className={Styles.cardHeader}>
                    <div className={Styles.platformIcon}>
                        {getPlatformIcon(platform)}
                    </div>
                    <span className={Styles.platformName}>{platform}</span>
                    {recommended && (
                        <Chip
                            label="For your device"
                            size="small"
                            sx={{ bgcolor: "#008080", color: "#fff", fontSize: "10px", height: 20, ml: 1 }}
                        />
                    )}
                    {hasFile && (
                        <span className={Styles.latestVersion}>
                            Latest: v{file.version}
                        </span>
                    )}
                </div>

                <div className={Styles.cardContent}>
                    {isAdmin ? (
                        <div className={Styles.uploadSection}>
                            {canUpload && (
                                <>
                                    <span className={Styles.uploadFormTitle}>
                                        <CloudUpload sx={{ fontSize: 15 }} /> Upload New Version
                                    </span>

                                    {hasVariants && (
                                        <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                                            <InputLabel>Build</InputLabel>
                                            <Select
                                                value={uploadVariants[platform]}
                                                label="Build"
                                                onChange={(e) =>
                                                    setUploadVariants((prev) => ({ ...prev, [platform]: e.target.value }))
                                                }
                                            >
                                                {variants.map((variant) => (
                                                    <MenuItem key={variant} value={variant}>
                                                        {variant}
                                                        {VARIANT_HINTS[variant] && (
                                                            <Typography component="span" sx={{ ml: 1, fontSize: '11px', color: '#9CA3AF' }}>
                                                                — {VARIANT_HINTS[variant]}
                                                            </Typography>
                                                        )}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <TextField
                                        size="small"
                                        placeholder="Version (e.g., 1.0.0)"
                                        value={versions[platform]}
                                        onChange={(e) => handleVersionChange(platform, e.target.value)}
                                        fullWidth
                                        sx={{ mb: 1 }}
                                    />

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <input
                                            type="file"
                                            style={{ display: 'none' }}
                                            accept={ACCEPT_BY_PLATFORM[platform] || "*/*"}
                                            ref={(el) => (fileInputRefs.current[platform] = el)}
                                            onChange={(e) => handleFileSelect(platform, e)}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            onClick={() => fileInputRefs.current[platform]?.click()}
                                            sx={{
                                                color: '#008080',
                                                borderColor: '#008080',
                                                '&:hover': { borderColor: '#006666', bgcolor: '#F0FAFA' }
                                            }}
                                        >
                                            {selectedFiles[platform] ? 'Change File' : 'Select File'}
                                        </Button>

                                        <Button
                                            variant="contained"
                                            size="small"
                                            fullWidth
                                            onClick={() => handleUpload(platform)}
                                            disabled={uploadLoading[platform] || !selectedFiles[platform] || !versions[platform]}
                                            startIcon={uploadLoading[platform] ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                                            sx={{
                                                bgcolor: '#008080',
                                                '&:hover': { bgcolor: '#006666' }
                                            }}
                                        >
                                            Upload
                                        </Button>
                                    </Box>

                                    {selectedFiles[platform] && (
                                        <div className={Styles.selectedFileChip}>
                                            <CloudUpload />
                                            <span>{selectedFiles[platform].name}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Version Management */}
                            <div className={Styles.versionHistory}>
                                <Typography className={Styles.historyTitle}>
                                    Manage Versions ({platformVersions.length})
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={selectedVersion?._id || ""}
                                            onChange={(e) => handleVersionSelect(platform, e.target.value)}
                                            displayEmpty
                                            sx={{ fontSize: '13px' }}
                                        >
                                            <MenuItem value="" disabled>Select version</MenuItem>
                                            {platformVersions.map((v) => (
                                                <MenuItem key={v._id} value={v._id}>
                                                    {hasVariants ? `${variantOf(v)} · ` : ""}v{v.version} - {formatDate(v.uploadedAt)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Tooltip title="Download">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownload(platform, selectedVersion)}
                                                disabled={!selectedVersion}
                                                sx={{ color: '#008080' }}
                                            >
                                                <Download fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {canRemove && (
                                        <Tooltip title="Delete">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(platform, selectedVersion?._id)}
                                                    disabled={!selectedVersion}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                            </div>
                        </div>
                    ) : (
                        <div className={Styles.downloadSection}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {hasVariants ? "Choose your Mac type, then a version." : "Select a version to download."}
                            </Typography>

                            {hasVariants && (
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Which Mac do you have?</InputLabel>
                                    <Select
                                        value={selectedVariant}
                                        label="Which Mac do you have?"
                                        onChange={(e) => handleVariantSelect(platform, e.target.value)}
                                        renderValue={(value) => value}
                                    >
                                        {variants.map((variant) => (
                                            <MenuItem key={variant} value={variant}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{variant}</Typography>
                                                    {VARIANT_HINTS[variant] && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {VARIANT_HINTS[variant]}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControl fullWidth size="small">
                                <InputLabel>Version</InputLabel>
                                <Select
                                    value={selectedVersion?._id || ""}
                                    onChange={(e) => handleVersionSelect(platform, e.target.value)}
                                    label="Version"
                                >
                                    {downloadVersions.map((v) => (
                                        <MenuItem key={v._id} value={v._id}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <Typography variant="body2" fontWeight={600}>v{v.version}</Typography>
                                                <Typography variant="caption" color="text.secondary">{formatDate(v.uploadedAt)}</Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                    {downloadVersions.length === 0 && (
                                        <MenuItem disabled value="">
                                            {hasVariants ? `No ${selectedVariant} build available` : "No versions available"}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>

                            {selectedVersion && (
                                <Box sx={{ mt: 2, p: 1.75, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #EAECF0' }}>
                                    {hasVariants && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>BUILD</Typography>
                                            <Typography sx={{ fontSize: '11px', color: '#008080', fontWeight: 700 }}>
                                                {variantOf(selectedVersion)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>UPLOADED BY</Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>
                                            {selectedVersion.uploadedBy?.firstName} {selectedVersion.uploadedBy?.lastName}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>DATE</Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>
                                            {formatDate(selectedVersion.uploadedAt)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                startIcon={<CloudDownload />}
                                onClick={() => handleDownload(platform)}
                                disabled={!selectedVersion}
                                sx={{
                                    mt: 2,
                                    py: 1.25,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    boxShadow: '0 6px 16px rgba(0, 128, 128, 0.28)',
                                    background: 'linear-gradient(135deg, #008080 0%, #00A3A3 100%)',
                                    '&:hover': { background: 'linear-gradient(135deg, #006666 0%, #008080 100%)' }
                                }}
                            >
                                Download v{selectedVersion?.version}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading && !appFiles.Mac) { // Initial loading
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
                <CircularProgress sx={{ color: '#008080' }} />
            </Box>
        );
    }

    // Customers get the Claude-style download experience; admins keep the
    // upload / version-management dashboard.
    if (!isAdmin) {
        return renderDownloadView();
    }

    return (
        <div className={Styles.pageContainer}>
            <div className={Styles.pageHeader}>
                <div className={Styles.title}>
                    {canUpload ? "Manage Applications" : "Download Applications"}
                </div>
                {!isAdmin && (
                    <Chip
                        label={userOS ? `Recommended for your ${userOS} device` : "Select your platform below"}
                        color="primary"
                        variant="outlined"
                        sx={{ borderColor: '#008080', color: '#008080' }}
                    />
                )}
            </div>

            <div className={Styles.contentGrid}>
                {(() => {
                    const defaultOrder = ["Windows", "Mac", "Linux"];
                    // For the download view, put the user's detected OS first and flag it.
                    const order = !isAdmin && userOS
                        ? [userOS, ...defaultOrder.filter((p) => p !== userOS)]
                        : defaultOrder;
                    return order.map((platform) =>
                        renderPlatformCard(platform, !isAdmin && platform === userOS)
                    );
                })()}
            </div>

            <Dialog
                open={openDeletePopup}
                onClose={() => setOpenDeletePopup(false)}
                PaperProps={{
                    style: {
                        width: "100%",
                        height: "fit-content",
                        padding: 20,
                    },
                }}
                fullWidth
                maxWidth="xs"
            >
                <Box display="flex" justifyContent="flex-end">
                    <CloseIcon
                        className={Styles.Close_Icon}
                        onClick={() => setOpenDeletePopup(false)}
                    />
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box padding={1}>
                        <img
                            src={ConfirmDelete_Icon}
                            alt="ConfirmDelete_Icon"
                            height={50}
                        />
                    </Box>
                    <Typography
                        sx={{ fontSize: "20px", fontWeight: 700, color: "#3F3F46" }}
                    >
                        Are you sure ?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            textAlign: "center",
                            padding: "0 50px",
                        }}
                    >
                        Are you sure you want to delete this version? This action cannot be
                        undone.
                    </Typography>
                    <Box display="flex" flexDirection="row" mt={2} gap={2}>
                        <Button
                            onClick={() => setOpenDeletePopup(false)}
                            className={Styles.cancelButton}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            sx={{ color: "white !important" }}
                            className={Styles.saveButton}
                        >
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </div>
    );
};

export default AppDownloadUpload;
